import classNames from 'classnames/dedupe'
import { Fragment } from 'react'
import Col from '../../components/basic/Col'
import { getRootUrl } from '../../modules/auth'
import LinkLocale from '../../modules/i18n/LinkLocale'
import { s_footer, s_menu } from '../../modules/i18n/strings/auto/wording'
import useTranslation from '../../modules/i18n/useTranslation'
import {
  IMG_FOOTER_FACEBOOK,
  IMG_FOOTER_INSTGRAM,
  IMG_FOOTER_NAVER_BLOG,
  IMG_FOOTER_YOUTUBE,
} from '../../modules/images'
import { FOOTER_LOGO_KCAB } from '../../modules/svg/svg'
import urls from '../../modules/urls'
import { COUNTRY_ALPHA2_CODE } from '../../modules/vars'
import useUserStore from '../../store/useUserStore'
import CenterContainer from './CenterContainer'
import VWContainer from './VWContainer'

interface FooterProps {
  dark?: boolean
  white?: boolean
  border?: boolean
  overlay?: boolean
  addClass?: string
  tutor?: boolean
  showMenu?: boolean
  up?: number
}

interface IFooterDoc {
  subtitle: string
  socials: IFooterDocSocials[]
  menu: IFooterDocMenu[] | null
  store: IFooterDocStore
  links?: IFooterDocLink[]
  info: string[]
  menuTutor: any | null
}

interface IFooterDocMenu {
  list: IFooterDocLink[]
}

interface IFooterDocStore {
  google: string
  apple: string
}

interface IFooterDocSocials {
  src: string
  href: string
}

interface IFooterDocLink {
  none?: boolean
  header: string
  links: IFooterDocLinkItem[]
}
interface IFooterDocLinkItem {
  none?: boolean
  name: string
  href: string
  type?: string
}

const Footer = ({ showMenu, dark, white, overlay, addClass, tutor }: FooterProps) => {
  const { t, isLocaleKO, locale, isLocaleEN } = useTranslation()

  const bgClass = white ? 'bg-white' : dark ? 'bg-black' : 'bg-light'
  const blogUrl = `/${locale}${urls.tutor.landing.blog.index}`
  const instaUrl = 'https://www.instagram.com/ringletutor/'
  const facebookUrl = 'https://www.facebook.com/groups/ringletutor'

  const currentGeo = useUserStore((state) => state.session.currentGeo)
  const isKR = currentGeo?.country_code == COUNTRY_ALPHA2_CODE.KR

  const tutorDoc: IFooterDoc = {
    subtitle: '1:1 Online English Learning Service',
    socials: [
      {
        src: IMG_FOOTER_NAVER_BLOG,
        href: blogUrl,
      },
      {
        src: IMG_FOOTER_INSTGRAM,
        href: instaUrl,
      },
      {
        src: IMG_FOOTER_FACEBOOK,
        href: facebookUrl,
      },
    ],
    menu: null,
    store: {
      google: 'https://play.google.com/store/apps/details?id=com.ringletutor',
      apple: 'https://itunes.apple.com/app/ringle-tutor/id1207702138?mt=8',
    },
    menuTutor: [
      {
        list: [
          {
            header: 'SERVICE',
            links: [
              {
                name: 'About Us',
                href: urls.tutor.landing.about,
                type: null,
              },
              {
                name: 'FAQ',
                href: `https://www.ringletutor.com/en/tutor/landing/help`,
                type: 'blank',
              },
            ],
          },
          {
            header: 'Student',
            links: [
              {
                name: 'I am a student',
                href: urls.oneOnOne.index,
                type: null,
              },
            ],
          },
        ],
      },
      {
        list: [
          {
            header: 'COMMUNITY',
            links: [
              {
                name: 'Blog',
                href: blogUrl,
                type: 'blank',
              },
              {
                name: 'Facebook',
                href: facebookUrl,
                type: 'blank',
              },
              {
                name: 'Instagram',
                href: instaUrl,
                type: 'blank',
              },
            ],
          },
        ],
      },
      {
        list: [
          {
            header: 'Policy',
            links: [
              {
                name: 'Privacy Policy',
                href: urls.tutor.policy.privacy,
                type: null,
              },
            ],
          },
        ],
      },
    ],
    info: [
      'Ringle English Education Service (Ringle Inc.)',
      '400 Concar Dr. San Mateo, CA, United States, 94402',
      '',
      'tutor@ringleplus.com',
    ],
  }
  const studentDoc: IFooterDoc = {
    subtitle: t(s_footer.beyond_offline_new_standards),
    socials: [
      {
        src: IMG_FOOTER_YOUTUBE,
        href: 'https://www.youtube.com/channel/UCG2DUiUkDuQckaRjkxqFUog/featured',
      },
      {
        src: IMG_FOOTER_INSTGRAM,
        href: 'https://www.instagram.com/ringleedu/',
      },
      {
        src: IMG_FOOTER_FACEBOOK,
        href: 'https://www.facebook.com/ringleedu',
      },
    ],
    store: {
      google: 'https://play.google.com/store/apps/details?id=com.ringle',
      apple: 'https://apps.apple.com/kr/app/ringle/id1189876556?l=en&mt=8',
    },
    menu: [
      {
        list: [
          {
            header: t(s_menu.landing.conversation), // what is ringle
            links: [
              {
                name: t(s_menu.landing.service_intro),
                href: urls.student.landing.main(getRootUrl()),
                type: null,
              },
              {
                name: t(s_menu.landing.tutor),
                href: urls.oneOnOne.tutor,
                type: null,
              },
              {
                name: t(s_menu.landing.lesson_materials),
                href: urls.oneOnOne.material,
              },
              {
                name: t(s_menu.landing.lesson_system),
                href: urls.student.landing.system,
              },
              {
                name: t(s_footer.pricing),
                href: urls.student.landing.pricing,
              },
            ],
          },
        ],
      },
      {
        list: [
          {
            header: t(s_menu.landing.solution), //Ringle for
            links: [
              {
                name: t(s_menu.landing.office_worker),
                href: urls.student.landing.case.customer.professional,
                type: null,
              },
              {
                name: t(s_menu.landing.live_abroad),
                href: urls.student.landing.case.customer.liveAbroad,
              },
              {
                name: t(s_menu.landing.study_abroad),
                href: urls.student.landing.case.customer.studyAbroad,
              },
              {
                name: t(s_menu.landing.business),
                href: urls.student.landing.case.purpose.business,
              },
              {
                name: t(s_menu.landing.prep_interview),
                href: urls.student.landing.case.purpose.interview,
              },
              {
                name: t(s_menu.landing.correction),
                href: urls.student.landing.case.purpose.correction,
              },
              {
                name: t(s_menu.landing.beginner),
                href: urls.student.landing.case.customer.beginner,
              },
            ],
          },
        ],
      },
      {
        list: [
          {
            header: t(s_footer.b2b), //business
            links: [
              {
                name: '수출 바우처',
                href: urls.student.landing.voucher,
                type: null,
                none: !isLocaleKO,
              },
              {
                name: t(s_footer.business_inquiry),
                href: urls.b2b.index,
                type: null,
              },
            ],
          },
          {
            header: 'Tutoring', // tutoring
            links: [
              {
                name: 'Become a Tutor',
                href: urls.tutor.landing.home,
              },
            ],
            none: !isLocaleEN,
          },
        ],
      },
      {
        list: [
          {
            header: t(s_footer.companay), //company
            links: [
              {
                name: t(s_footer.help_center),
                type: null,
                href: urls.student.landing.faq,
                none: !isKR,
              },
              {
                name: t(s_footer.ringle_team),
                type: null,
                href: urls.company.index,
              },
              {
                name: t(s_footer.career),
                type: 'blank',
                href: 'https://career.ringleplus.com',
                none: !isKR,
              },
              {
                name: isLocaleKO ? '링글 블로그' : 'Ringle Blog',
                type: null,
                href: urls.student.landing.blog.index,
              },
            ],
          },
        ],
      },
    ],
    menuTutor: null,
    links: [],
    info: [],
  }
  // 개인정보
  const policyMenu: IFooterDocLink = {
    header: t(s_footer.policy),
    links: [
      {
        name: t(s_footer.terms_of_privacy),
        href: urls.student.policy.privacy,
        type: null,
      },
      {
        name: t(s_footer.terms_of_service),
        href: urls.student.policy.terms,
        type: null,
      },
    ],
  }
  const companyInfo: any[] = [
    { title: tutor ? 'Ringle English Education Service (Ringle Inc.)' : t(s_footer.ringle_company) },
    [
      tutor ? '400 Concar Dr. San Mateo, CA, United States, 94402' : t(s_footer.ringle_address),
      tutor ? '' : t(s_footer.ringle_phone),
      tutor ? 'tutor@ringleplus.com' : t(s_footer.ringle_ceo),
    ],
  ]
  const doc = tutor ? tutorDoc : studentDoc

  return (
    <>
      <VWContainer bgColor="bg-black">
        <CenterContainer>
          <section className={classNames({ 'bg-black': dark }, { 'pt-16': overlay }, addClass)}>
            <footer className={classNames('py-48 md:py-96', bgClass)}>
              <div>
                {showMenu && doc.menu && (
                  <div className="row mb-[60px]">
                    {doc.menu?.map((item, i) => (
                      <Fragment key={i}>
                        <Col clx="col-12 md:col-6 lg:col">
                          {item.list
                            .filter((e) => !e.none)
                            .map((e, j) => (
                              <Fragment key={j}>
                                {e.header ? (
                                  <h4 className={classNames({ 'text-white': dark }, 'text-h4 mb-[10px] mt-[48px]')}>
                                    {e.header}
                                  </h4>
                                ) : (
                                  <h4 className={classNames({ 'text-white': dark }, 'text-h4 smd:hidden mb-[16px]')}>
                                    &nbsp;
                                  </h4>
                                )}
                                <ul>
                                  {e.links
                                    .filter((e) => !e.none)
                                    .map((link, i) => {
                                      return (
                                        <li key={i} className="mb-[8px] text-gray-300">
                                          {link.type === 'blank' ? (
                                            <a href={link.href} target="_blank" rel="noreferrer">
                                              {link.name}
                                            </a>
                                          ) : (
                                            <LinkLocale href={link.href}>{link.name}</LinkLocale>
                                          )}
                                        </li>
                                      )
                                    })}
                                </ul>
                                {i === doc.menu?.length - 1 && isLocaleKO && (
                                  <Col clx="col-12 md:col-6 lg:col">
                                    <ul className="flex flex-row items-center">
                                      {doc.socials?.map((social, i) => (
                                        <li key={i} className="mr-[8px] w-[40px]">
                                          <a
                                            target="_blank"
                                            href={social.href}
                                            className="text-decoration-none"
                                            rel="noreferrer"
                                          >
                                            <img
                                              src={social.src}
                                              className="list-social-icon"
                                              alt={social.href}
                                              width="45px"
                                            />
                                          </a>
                                        </li>
                                      ))}
                                    </ul>
                                  </Col>
                                )}
                              </Fragment>
                            ))}
                        </Col>
                      </Fragment>
                    ))}
                  </div>
                )}
                <div className="row">
                  <Col clx="col-12 md:col-6 order-1 md:order-0">
                    {tutor || <img src={FOOTER_LOGO_KCAB} className="mb-[68px] max-w-none" />}
                    <img src="/assets/img/logo/text_logo_white.svg" className="mb-12 max-w-none" />

                    {/* 회사 정보 */}
                    <>
                      <Col mb24>
                        <div>
                          <p className="text-sBody text-white">{companyInfo[0].title}</p>
                          {companyInfo[1].map((row, i) => (
                            <p key={i} className="text-sBody text-gray-300">
                              <Fragment key={i}>{row}</Fragment>
                            </p>
                          ))}
                        </div>
                      </Col>
                    </>
                    <div className="mb-[24px] flex flex-row items-center">
                      {tutor ||
                        policyMenu.links.map((item, i) => (
                          <Fragment key={i}>
                            <LinkLocale href={item.href}>
                              <div className="text-sBody text-white">{item.name}</div>
                            </LinkLocale>
                            <div
                              className={classNames([i == 1 && 'hidden'], 'mx-[32px] h-[18px] w-[1px] bg-white')}
                            ></div>
                          </Fragment>
                        ))}
                    </div>

                    <div className="mt-[24px]">
                      <a
                        target="_blank"
                        href={doc.store.apple}
                        className="text-reset lift w-128 mr-12 inline-block"
                        rel="noreferrer"
                      >
                        <img src="/assets/img/buttons/button-app.webp" loading="lazy" className="h-full w-full" />
                      </a>
                      <a
                        target="_blank"
                        href={doc.store.google}
                        className="text-reset lift w-128 inline-block"
                        rel="noreferrer"
                      >
                        <img src="/assets/img/buttons/button-play.webp" loading="lazy" className="h-full w-full" />
                      </a>
                    </div>
                  </Col>
                  <Col clx="col-12 md:col-6 order-0 md:order-1">
                    <div className="row">
                      {tutor &&
                        doc.menuTutor
                          ?.filter((et) => !et.none)
                          .map((et, i) => (
                            <Col key={i} clx="col-4" px20>
                              {et.list.map((e, j) => (
                                <Fragment key={j}>
                                  {e.header && (
                                    <h5
                                      className={classNames('font-weight-bold text-uppercase mb-4', {
                                        'text-gray-200': dark,
                                      })}
                                    >
                                      {e.header}
                                    </h5>
                                  )}
                                  {e.links && (
                                    <ul className="list-unstyled text-muted mb-24">
                                      {e.links
                                        ?.filter((e) => !e.none)
                                        .map((link, i) => (
                                          <li key={i} className="mb-12">
                                            {link.type == 'blank' ? (
                                              <a
                                                href={link.href}
                                                rel="noreferrer"
                                                target="_blank"
                                                className="text-gray-300"
                                              >
                                                {link.name}
                                              </a>
                                            ) : (
                                              <LinkLocale href={link.href} className="text-gray-300">
                                                {link.name}
                                              </LinkLocale>
                                            )}
                                          </li>
                                        ))}
                                    </ul>
                                  )}
                                </Fragment>
                              ))}
                            </Col>
                          ))}
                    </div>
                  </Col>
                </div>
              </div>
            </footer>
          </section>
        </CenterContainer>
      </VWContainer>
    </>
  )
}

export default Footer
